import { Injectable } from '@angular/core';

type ImageSource = ImageBitmap | HTMLImageElement;

@Injectable({
    providedIn: 'root'
})
export class ImageOptimizerService {

    private readonly WEBP_QUALITY = 0.80;
    private readonly MAX_DIMENSION = 1920;
    private readonly MIN_RECOMPRESS_SIZE = 150 * 1024;

    // Optimiza archivo de imagen a WebP con fallback seguro
    public async optimize(file: File): Promise<File> {
        if (this.isBypassable(file)) return file;
        try {
            return await this.compressImage(file);
        } catch {
            return file;
        }
    }

    // Determina si el archivo debe omitir optimizacion
    private isBypassable(file: File): boolean {
        if (!file?.type?.startsWith('image/')) return true;
        if (file.type === 'image/svg+xml' || file.type === 'image/gif') return true;
        return file.type === 'image/webp' && file.size < this.MIN_RECOMPRESS_SIZE;
    }

    // Procesa decodificacion, redimension y compresion
    private async compressImage(file: File): Promise<File> {
        const source = await this.loadImageSource(file);
        try {
            const dim = this.calculateDimensions(source.width, source.height);
            const blob = await this.renderToBlob(source, dim.width, dim.height);
            if (!blob || blob.size >= file.size) return file;
            return this.createOptimizedFile(blob, file);
        } finally {
            this.cleanSource(source);
        }
    }

    // Calcula dimensiones conservando la relacion de aspecto
    private calculateDimensions(width: number, height: number): { width: number; height: number } {
        const scale = Math.min(1, this.MAX_DIMENSION / (width || 1), this.MAX_DIMENSION / (height || 1));
        return {
            width: Math.round(width * scale),
            height: Math.round(height * scale)
        };
    }

    // Carga origen visual mediante ImageBitmap o Image nativo
    private async loadImageSource(file: File): Promise<ImageSource> {
        if (typeof createImageBitmap === 'function') {
            return await createImageBitmap(file);
        }
        return await this.loadViaImageElement(file);
    }

    // Carga imagen tradicional mediante ObjectURL como fallback
    private loadViaImageElement(file: File): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const url = URL.createObjectURL(file);
            img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
            img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
            img.src = url;
        });
    }

    // Dibuja en canvas y comprime a WebP
    private renderToBlob(source: ImageSource, width: number, height: number): Promise<Blob | null> {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return Promise.resolve(null);
        ctx.drawImage(source, 0, 0, width, height);
        return new Promise((resolve) => {
            canvas.toBlob((blob) => resolve(blob), 'image/webp', this.WEBP_QUALITY);
        });
    }

    // Construye archivo File WebP con el nombre original
    private createOptimizedFile(blob: Blob, original: File): File {
        const newName = original.name.replace(/\.[^/.]+$/, '') + '.webp';
        return new File([blob], newName, { type: 'image/webp' });
    }

    // Libera recursos del origen si es ImageBitmap
    private cleanSource(source: ImageSource): void {
        if ('close' in source && typeof source.close === 'function') {
            source.close();
        }
    }
}
